import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Dimensions,
  Platform,
  TouchableOpacity,
  Modal,
  Pressable,
  Image as RNImage,
  ActivityIndicator,
} from 'react-native';
import { getTile3dNativeStyle, getTile3dWebStyle } from '../utils/mainPageTileStyles';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  dedupeCoursesForDropdown,
} from '../utils/coursesCatalog';

const COURSE_TILE_IMAGE = require('../../assets/ChatGPT Image Jan 28, 2026, 12_32_42 AM.png');
const BODY_DUMMY_IMAGE = require('../../assets/ChatGPT Image Mar 21, 2026, 01_50_23 AM.png');

const VIEW_MODE_TILES = 'tiles';
const VIEW_MODE_BODY = 'body';

/**
 * 2×2 grid icon for “tiles” view — hollow cells with gold borders (readable on light + dark trays).
 */
function TilesViewIcon({ selected }) {
  const gap = 4;
  const cell = 11;
  const rowW = cell * 2 + gap;
  const borderW = selected ? 2.5 : 2;
  const fill = 'transparent';
  const outline = '#d4af37';
  const cellStyle = {
    width: cell,
    height: cell,
    borderRadius: 2,
    backgroundColor: fill,
    borderWidth: borderW,
    borderColor: outline,
  };
  return (
    <View
      style={{
        width: rowW,
        height: rowW,
        justifyContent: 'space-between',
        backgroundColor: 'transparent',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          width: rowW,
          justifyContent: 'space-between',
        }}
      >
        <View style={cellStyle} />
        <View style={cellStyle} />
      </View>
      <View
        style={{
          flexDirection: 'row',
          width: rowW,
          justifyContent: 'space-between',
        }}
      >
        <View style={cellStyle} />
        <View style={cellStyle} />
      </View>
    </View>
  );
}

const DESKTOP_BREAKPOINT = 768;
const H_PADDING = 20;
const TILE_GAP = 10;

/** Keep in sync with `CourseBuilder` COURSE_TYPES */
const COURSE_TYPES = [
  'External',
  'Internal',
  'Weapons',
  'Dim Mak',
  'Fitness',
  'Shinobi',
];

const FILTER_ALL = 'All';

function collapseSpaces(s) {
  return String(s ?? '').trim().replace(/\s+/g, ' ');
}

/** Align DB `course_type` with filter labels (e.g. external → External). Collapses internal spaces so "Dim  Mak" matches Dim Mak. */
function canonicalCourseType(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const normalized = collapseSpaces(String(raw));
  if (!normalized) return null;
  const k = normalized.toLowerCase();
  const hit = COURSE_TYPES.find((ct) => ct.toLowerCase() === k);
  if (hit) return hit;
  return normalized;
}

function courseTypesMatch(a, b) {
  if (a == null || b == null) return false;
  return (
    collapseSpaces(String(a)).toLowerCase() === collapseSpaces(String(b)).toLowerCase()
  );
}

/**
 * Body-diagram tap targets (% of overlay — tune if artwork layout changes).
 * Dummy’s right fist = viewer’s left; dummy’s left open hand = viewer’s right.
 * Head = skull only (avoids neck / overlap with raised-hand hotspots).
 * Palm = thin strip on outer (screen-right) edge of the open palm; fingers = column from knuckles toward tips.
 * Legs = shins + feet (starts below thigh band).
 * Order: fingers after palm so the finger zone wins on overlap.
 */
const BODY_HOTSPOTS = [
  {
    id: 'head',
    label: "Dummy's head",
    courseNames: ['Iron Head'],
    top: '8%',
    left: '34%',
    width: '28%',
    height: '13%',
  },
  {
    id: 'feet',
    label: "Dummy's shins & feet",
    courseNames: ['Iron Leg'],
    top: '64%',
    left: '5%',
    width: '90%',
    height: '34%',
  },
  {
    id: 'right_fist',
    label: "Dummy's right fist",
    courseNames: ['Iron Fist'],
    top: '19%',
    left: '31%',
    width: '7%',
    height: '8%',
  },
  {
    id: 'left_palm',
    label: "Dummy's left palm",
    courseNames: ['Iron Palm'],
    top: '18%',
    left: '64%',
    width: '6%',
    height: '12%',
  },
  {
    id: 'left_fingers',
    label: "Dummy's left hand — fingers",
    courseNames: ['Iron Finger', 'Yao Jao Moon'],
    top: '8%',
    left: '62%',
    width: '11%',
    height: '17%',
  },
];

function parsePercentToFraction(p) {
  return parseFloat(String(p).replace('%', '')) / 100;
}

/**
 * Visible yellow circle (centered in % box) + full-rect hit target so taps match the artwork.
 */
function BodyHotspotPressable({
  hotspot,
  shellW,
  shellH,
  courses,
  stackIndex,
  styles,
  headingColor,
  onOpenModal,
}) {
  const boxW = parsePercentToFraction(hotspot.width) * shellW;
  const boxH = parsePercentToFraction(hotspot.height) * shellH;
  const diameter = Math.max(28, Math.min(boxW, boxH) * 0.88);

  return (
    <Pressable
      {...(Platform.OS === 'web' ? { dataSet: { bodyHotspot: hotspot.id } } : {})}
      style={({ pressed }) => [
        styles.bodyHotspot,
        {
          top: hotspot.top,
          left: hotspot.left,
          width: hotspot.width,
          height: hotspot.height,
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10 + stackIndex,
        },
        Platform.OS === 'web' && styles.bodyHotspotPressableWeb,
        pressed && styles.bodyHotspotPressed,
      ]}
      onPress={() => {
        const matched = matchUserCoursesFromHotspot(hotspot.courseNames, courses);
        onOpenModal({ hotspot, matched });
      }}
      accessibilityRole="button"
      accessibilityLabel={`${hotspot.label}. Tap for related courses.`}
      hitSlop={Platform.OS === 'web' ? undefined : { top: 4, bottom: 4, left: 4, right: 4 }}
    >
      <View
        pointerEvents="none"
        {...(Platform.OS === 'web' ? { dataSet: { hotspotCircle: 'true' } } : {})}
        style={[
          styles.bodyHotspotCircle,
          {
            width: diameter,
            height: diameter,
            borderRadius: diameter / 2,
            borderColor: headingColor,
          },
        ]}
      />
    </Pressable>
  );
}

/** Normalize `students.courses` from API (TEXT[], JSON string, or legacy shapes). */
function normalizeCoursesFromRow(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((c) => String(c).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
    if (s.startsWith('{') && s.endsWith('}')) {
      return s
        .slice(1, -1)
        .split(',')
        .map((t) => t.replace(/^"|"$/g, '').trim())
        .filter(Boolean);
    }
    return [s];
  }
  return [];
}

/** Match catalog course names to strings on the student profile (case-insensitive). */
function matchUserCoursesFromHotspot(canonicalNames, userCourses) {
  if (!Array.isArray(userCourses) || !canonicalNames.length) return [];
  const byLower = new Map();
  userCourses.forEach((c) => {
    const s = typeof c === 'string' ? c.trim() : '';
    if (s) byLower.set(s.toLowerCase(), s);
  });
  const out = [];
  canonicalNames.forEach((target) => {
    const t = String(target).trim().toLowerCase();
    const hit = byLower.get(t);
    if (hit) out.push(hit);
  });
  return out;
}

/** Title underline colours by course type (matches catalog `course_type`, case-insensitive). */
const COURSE_TYPE_UNDERLINE_HEX = {
  External: '#e6c229',
  Internal: '#d32f2f',
  Weapons: '#8e24aa',
  'Dim Mak': '#f57c00',
  Fitness: '#1976d2',
};

/**
 * @returns {{ kind: 'solid', color: string } | { kind: 'shinobi' } | { kind: 'default' }}
 */
function getCourseTypeUnderlineSpec(courseTypeRaw) {
  if (courseTypeRaw == null || String(courseTypeRaw).trim() === '') {
    return { kind: 'default' };
  }
  const key = collapseSpaces(String(courseTypeRaw)).toLowerCase();
  if (key === 'shinobi') return { kind: 'shinobi' };
  if (key === 'external') return { kind: 'solid', color: COURSE_TYPE_UNDERLINE_HEX.External };
  if (key === 'internal') return { kind: 'solid', color: COURSE_TYPE_UNDERLINE_HEX.Internal };
  if (key === 'weapons') return { kind: 'solid', color: COURSE_TYPE_UNDERLINE_HEX.Weapons };
  if (key === 'dim mak') return { kind: 'solid', color: COURSE_TYPE_UNDERLINE_HEX['Dim Mak'] };
  if (key === 'fitness') return { kind: 'solid', color: COURSE_TYPE_UNDERLINE_HEX.Fitness };
  return { kind: 'default' };
}

function CourseTitleWithUnderline({ courseName, catalogType, theme, titleStyle, underlineStyles }) {
  const spec = getCourseTypeUnderlineSpec(catalogType);
  /** Unknown / unmapped types: neutral line — not heading gold (reads as “all External”). */
  const defaultLine = theme.isDarkMode ? '#757575' : '#9E9E9E';
  const titleWebProps =
    Platform.OS === 'web' ? { dataSet: { courseTileTitle: 'true' } } : {};
  const titleBoxWebProps =
    Platform.OS === 'web' ? { dataSet: { myCoursesTileTitleBox: 'true' } } : {};

  if (spec.kind === 'shinobi') {
    return (
      <View style={underlineStyles.titleUnderlineWrap} {...titleBoxWebProps}>
        <Text style={titleStyle} numberOfLines={2} {...titleWebProps}>
          {courseName}
        </Text>
        <View style={underlineStyles.shinobiUnderlineStack}>
          <View
            style={underlineStyles.shinobiUnderlineWhite}
            {...(Platform.OS === 'web' ? { dataSet: { shinobiUnderlineWhite: 'true' } } : {})}
          />
          <View
            style={underlineStyles.shinobiUnderlineBlack}
            {...(Platform.OS === 'web' ? { dataSet: { shinobiUnderlineBlack: 'true' } } : {})}
          />
        </View>
      </View>
    );
  }

  const lineColor = spec.kind === 'solid' ? spec.color : defaultLine;
  return (
    <View
      style={[
        underlineStyles.titleUnderlineWrap,
        { borderBottomWidth: 3, borderBottomColor: lineColor },
      ]}
      {...titleBoxWebProps}
    >
      <Text style={titleStyle} numberOfLines={2} {...titleWebProps}>
        {courseName}
      </Text>
    </View>
  );
}

function lookupCatalogCourseType(courseName, nameToType) {
  if (!nameToType?.exact || !nameToType.lower) return null;
  const key = collapseSpaces(courseName);
  if (!key) return null;
  const lower = key.toLowerCase();
  return (
    nameToType.exact.get(key) ?? nameToType.lower.get(lower) ?? null
  );
}

function CourseTileButton({
  courseName,
  theme,
  styles,
  nameToType,
  onPress,
  tileExtraStyle,
  preferTouchable = false,
}) {
  const catalogType = lookupCatalogCourseType(courseName, nameToType);
  const [touchPressed, setTouchPressed] = useState(false);
  const webTileDataSet =
    Platform.OS === 'web' ? { dataSet: { myCoursesTile: 'true' } } : {};

  const tileContent = (
    <>
      <Image
        source={COURSE_TILE_IMAGE}
        style={styles.tileImage}
        contentFit="contain"
      />
      <CourseTitleWithUnderline
        courseName={courseName}
        catalogType={catalogType}
        theme={theme}
        titleStyle={styles.tileTitle}
        underlineStyles={styles}
      />
    </>
  );

  if (preferTouchable) {
    return (
      <TouchableOpacity
        activeOpacity={1}
        style={[
          styles.tile,
          tileExtraStyle,
          Platform.OS === 'web' && getTile3dWebStyle(theme, touchPressed),
          Platform.OS !== 'web' && getTile3dNativeStyle(theme, touchPressed),
        ]}
        onPress={() => onPress?.(courseName)}
        onPressIn={() => setTouchPressed(true)}
        onPressOut={() => setTouchPressed(false)}
        {...webTileDataSet}
      >
        {tileContent}
      </TouchableOpacity>
    );
  }

  return (
    <Pressable
      style={({ pressed }) => [
        styles.tile,
        tileExtraStyle,
        Platform.OS === 'web' && getTile3dWebStyle(theme, pressed),
        Platform.OS !== 'web' && getTile3dNativeStyle(theme, pressed),
      ]}
      onPress={() => onPress?.(courseName)}
      {...webTileDataSet}
    >
      {tileContent}
    </Pressable>
  );
}

function useWindowWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setW(window.width);
    });
    return () => sub?.remove?.();
  }, []);
  return w;
}

export default function MyCoursesPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const width = useWindowWidth();

  const [user, setUser] = useState(route?.params?.user || null);
  const [userHydrating, setUserHydrating] = useState(() => {
    const p = route?.params;
    const hasUser = p?.user && typeof p.user === 'object' && p.user.id != null;
    const uid = p?.userId ?? p?.user?.id;
    return !hasUser && uid != null;
  });
  const [catalogCourses, setCatalogCourses] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState(FILTER_ALL);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODE_TILES);
  const [hotspotModal, setHotspotModal] = useState(null);

  const loadCatalogCourses = useCallback(() => {
    fetch(`${getApiUrl()}/api/courses`)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setCatalogCourses(dedupeCoursesForDropdown(data));
        else setCatalogCourses([]);
      })
      .catch(() => setCatalogCourses([]));
  }, []);

  useEffect(() => {
    loadCatalogCourses();
  }, [loadCatalogCourses]);

  useFocusEffect(
    useCallback(() => {
      loadCatalogCourses();
    }, [loadCatalogCourses])
  );

  useFocusEffect(
    useCallback(() => {
      const p = route?.params ?? {};
      const paramUser = p.user;
      if (paramUser && typeof paramUser === 'object' && paramUser.id != null) {
        setUser(paramUser);
        setUserHydrating(false);
        return undefined;
      }
      const uid = p.userId ?? paramUser?.id;
      if (uid == null) {
        setUserHydrating(false);
        return undefined;
      }
      let cancelled = false;
      setUserHydrating(true);
      fetch(`${getApiUrl()}/api/users/${uid}`)
        .then((res) => res.json())
        .then((data) => {
          if (cancelled) return;
          if (data && !data.error) setUser(data);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setUserHydrating(false);
        });
      return () => {
        cancelled = true;
      };
    }, [route?.params])
  );

  const courses = useMemo(() => {
    const catalogNames = catalogCourses
      .map((c) => collapseSpaces(c.course_name))
      .filter(Boolean);
    return [...catalogNames].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [catalogCourses]);

  /** course_name -> canonical course_type (matches dropdown labels) */
  const nameToType = useMemo(() => {
    const exact = new Map();
    const lower = new Map();
    catalogCourses.forEach((c) => {
      const name = collapseSpaces(c.course_name);
      if (!name) return;
      const t = canonicalCourseType(c.course_type);
      exact.set(name, t);
      lower.set(name.toLowerCase(), t);
    });
    return { exact, lower };
  }, [catalogCourses]);

  /** Collapsed name -> full course row (for navigation / id) */
  const catalogByCourseName = useMemo(() => {
    const exact = new Map();
    const lower = new Map();
    catalogCourses.forEach((c) => {
      const name = collapseSpaces(c.course_name);
      if (!name) return;
      const existing = exact.get(name);
      const preferLowerId =
        !existing || Number(c.id) < Number(existing.id);
      const row = preferLowerId ? c : existing;
      exact.set(name, row);
      lower.set(name.toLowerCase(), row);
    });
    return { exact, lower };
  }, [catalogCourses]);

  const filterOptions = useMemo(() => {
    const normalizedFromCatalog = [
      ...new Set(
        catalogCourses
          .map((c) => canonicalCourseType(c.course_type))
          .filter((t) => t != null && String(t).trim() !== '')
      ),
    ];
    const extras = normalizedFromCatalog
      .filter(
        (t) =>
          !COURSE_TYPES.some(
            (ct) => ct.toLowerCase() === String(t).trim().toLowerCase()
          )
      )
      .sort((a, b) => a.localeCompare(b));
    return [FILTER_ALL, ...COURSE_TYPES, ...extras];
  }, [catalogCourses]);

  const filteredCourses = useMemo(() => {
    if (selectedCourseType === FILTER_ALL) return courses;
    const selectedNorm = canonicalCourseType(selectedCourseType) ?? collapseSpaces(selectedCourseType);
    return courses.filter((cn) => {
      const key = collapseSpaces(cn);
      if (!key) return false;
      const t = nameToType.exact.get(key) ?? nameToType.lower.get(key.toLowerCase()) ?? null;
      const tNorm = t != null ? canonicalCourseType(t) ?? collapseSpaces(String(t)) : null;
      if (tNorm == null) return false;
      return courseTypesMatch(tNorm, selectedNorm);
    });
  }, [courses, selectedCourseType, nameToType]);

  const numCols = width >= DESKTOP_BREAKPOINT ? 3 : 2;
  const innerWidth = width - H_PADDING * 2;
  const tileWidth = (innerWidth - TILE_GAP * (numCols - 1)) / numCols;

  /** Explicit box for dummy + hotspots (RN Web can collapse aspectRatio-only images). */
  const bodyDiagramLayout = useMemo(() => {
    const shellW = Math.min(Math.max(innerWidth, 1), 520);
    const h = Math.min(
      Math.round((shellW * 4) / 3),
      Math.round(width * 0.85),
      640
    );
    return { shellW, h };
  }, [innerWidth, width]);

  const styles = useMemo(() => getStyles(theme, tileWidth), [theme, tileWidth]);

  const handleTilePress = useCallback(
    (courseName) => {
      if (!user?.id || !courseName) return;
      const key = collapseSpaces(courseName);
      if (!key) return;
      const row =
        catalogByCourseName.exact.get(key) ?? catalogByCourseName.lower.get(key.toLowerCase());
      if (!row?.id) return;
      setHotspotModal(null);
      navigation.navigate('MyCourseDetail', {
        user,
        courseId: row.id,
        courseName: collapseSpaces(row.course_name) || courseName,
      });
    },
    [user, navigation, catalogByCourseName]
  );

  return (
    <View style={styles.container} data-screen="my-courses" data-theme={theme.isDarkMode ? 'dark' : 'light'}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer} data-header-element="breadcrumb">
          <Text style={styles.breadcrumb}>My Courses</Text>
        </View>
        <View style={styles.profileIconContainer} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
        ]}
        showsVerticalScrollIndicator={false}
        {...(Platform.OS === 'web' ? { dataSet: { scrollContainer: 'my-courses' } } : {})}
      >
        {userHydrating ? (
          <View style={styles.emptyWrap}>
            <ActivityIndicator size="large" color={theme.colors.heading} />
            <Text style={[styles.emptySub, { marginTop: 16 }]}>
              Loading your profile…
            </Text>
          </View>
        ) : !user ? (
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyTitle}>Couldn&apos;t load profile</Text>
            <Text style={styles.emptySub}>
              Return to the main page and open My Courses again. If this keeps happening, check
              that the server is running.
            </Text>
          </View>
        ) : (
          <>
            <View
              style={styles.filterBarRow}
              {...(Platform.OS === 'web' ? { dataSet: { myCoursesFilterRow: 'true' } } : {})}
            >
              <View style={styles.filterBarLeft}>
                <Text style={[styles.filterLabel, { color: theme.colors.text }]}>
                  Course type
                </Text>
                <TouchableOpacity
                  style={[
                    styles.filterTrigger,
                    {
                      borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
                      backgroundColor: theme.isDarkMode ? theme.colors.surface : 'transparent',
                    },
                  ]}
                  onPress={() => setTypeDropdownOpen(true)}
                  activeOpacity={0.8}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { myCoursesFilterTrigger: 'true' } }
                    : {})}
                >
                  <Text
                    style={[styles.filterTriggerText, { color: theme.colors.heading }]}
                    numberOfLines={1}
                  >
                    {selectedCourseType}
                  </Text>
                  <Text style={[styles.filterChevron, { color: theme.colors.text }]}>▾</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.viewToggleWrap}>
                <Text style={[styles.viewToggleLabel, { color: theme.colors.text }]}>
                  View
                </Text>
                <View
                  style={[
                    styles.viewToggle,
                    theme.isDarkMode ? styles.viewToggleShellDark : styles.viewToggleShellLight,
                  ]}
                  {...(Platform.OS === 'web' ? { dataSet: { myCoursesViewToggle: 'true' } } : {})}
                >
                  <TouchableOpacity
                    style={[
                      styles.viewToggleHit,
                      Platform.OS === 'web' && styles.viewToggleHitWeb,
                      viewMode === VIEW_MODE_TILES
                        ? [
                            styles.viewToggleHitSelected,
                            styles.viewToggleHitSelectedTilesLight,
                          ]
                        : styles.viewToggleHitUnselected,
                    ]}
                    onPress={() => setViewMode(VIEW_MODE_TILES)}
                    activeOpacity={0.75}
                    accessibilityRole="button"
                    accessibilityLabel="Tiles view"
                    accessibilityState={{ selected: viewMode === VIEW_MODE_TILES }}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { myCoursesViewToggleTiles: 'true' } }
                      : {})}
                  >
                    <TilesViewIcon selected={viewMode === VIEW_MODE_TILES} />
                  </TouchableOpacity>
                  <View
                    style={[
                      styles.viewToggleDivider,
                      {
                        backgroundColor: theme.isDarkMode
                          ? 'rgba(212, 175, 55, 0.45)'
                          : 'rgba(212, 175, 55, 0.85)',
                      },
                    ]}
                  />
                  <TouchableOpacity
                    style={[
                      styles.viewToggleHit,
                      Platform.OS === 'web' && styles.viewToggleHitWeb,
                      viewMode === VIEW_MODE_BODY
                        ? [
                            styles.viewToggleHitSelected,
                            styles.viewToggleHitSelectedBodyLight,
                          ]
                        : styles.viewToggleHitUnselected,
                    ]}
                    onPress={() => setViewMode(VIEW_MODE_BODY)}
                    activeOpacity={0.75}
                    accessibilityRole="button"
                    accessibilityLabel="Body diagram view"
                    accessibilityState={{ selected: viewMode === VIEW_MODE_BODY }}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { myCoursesViewToggleBody: 'true' } }
                      : {})}
                  >
                    <View
                      style={[
                        styles.viewToggleBodyThumbRing,
                        viewMode === VIEW_MODE_BODY && styles.viewToggleBodyThumbRingSelected,
                      ]}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { myCoursesBodyThumbWrap: 'true' } }
                        : {})}
                    >
                      {/*
                       * RN Image (not expo-image): on web, expo-image in tiny flex thumbs often
                       * paints black until fail; RN maps require() to a real <img> reliably.
                       */}
                      <RNImage
                        source={BODY_DUMMY_IMAGE}
                        style={styles.viewToggleBodyThumb}
                        resizeMode="contain"
                      />
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {viewMode === VIEW_MODE_BODY ? (
              <View
                style={[styles.bodyDummyWrap, { width: innerWidth }]}
                nativeID={Platform.OS === 'web' ? 'myCoursesBodyDiagram' : undefined}
                testID={Platform.OS === 'web' ? 'myCoursesBodyDiagram' : undefined}
              >
                <View
                  style={[
                    styles.bodyDummyImageShell,
                    {
                      width: bodyDiagramLayout.shellW,
                      height: bodyDiagramLayout.h,
                      backgroundColor: '#ffffff',
                    },
                  ]}
                >
                  {Platform.OS === 'web' ? (
                    <>
                      {/*
                       * Web: global.css forces black on most css-view nodes; ImageBackground + nested
                       * Views stacked opaque black over the art. Use RN Image (real <img>) like the
                       * view-toggle thumb — see global.css #myCoursesBodyDiagram overrides.
                       */}
                      <RNImage
                        source={BODY_DUMMY_IMAGE}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: bodyDiagramLayout.shellW,
                          height: bodyDiagramLayout.h,
                          zIndex: 0,
                        }}
                        resizeMode="contain"
                      />
                    </>
                  ) : (
                    <Image
                      source={BODY_DUMMY_IMAGE}
                      style={{
                        width: bodyDiagramLayout.shellW,
                        height: bodyDiagramLayout.h,
                      }}
                      contentFit="contain"
                      transition={0}
                    />
                  )}
                  <View
                    style={[
                      styles.bodyHotspotLayer,
                      styles.bodyHotspotLayerAbove,
                      { pointerEvents: 'box-none' },
                    ]}
                  >
                    {BODY_HOTSPOTS.map((h, idx) => (
                      <BodyHotspotPressable
                        key={h.id}
                        hotspot={h}
                        shellW={bodyDiagramLayout.shellW}
                        shellH={bodyDiagramLayout.h}
                        courses={courses}
                        stackIndex={idx}
                        styles={styles}
                        headingColor={theme.colors.heading}
                        onOpenModal={setHotspotModal}
                      />
                    ))}
                  </View>
                </View>
              </View>
            ) : courses.length === 0 ? (
              <View style={styles.emptyWrap}>
                <Text style={styles.emptyTitle}>No courses yet</Text>
                <Text style={styles.emptySub}>
                  Your instructor can assign courses to your profile. Use the body view (right
                  side of View) to explore linked areas.
                </Text>
              </View>
            ) : filteredCourses.length === 0 ? (
              <View style={styles.emptyWrap}>
                <Text style={styles.emptyTitle}>No courses in this category</Text>
                <Text style={styles.emptySub}>
                  Try &quot;{FILTER_ALL}&quot; or another course type. Names must match the
                  school course catalog for type filtering.
                </Text>
              </View>
            ) : (
              <View
                style={[styles.tilesRow, { width: innerWidth, gap: TILE_GAP }]}
                {...(Platform.OS === 'web' ? { dataSet: { myCoursesTilesRow: 'true' } } : {})}
              >
                {filteredCourses.map((courseName, index) => (
                  <View
                    key={`${courseName}-${index}`}
                    style={[styles.tileWrap, { width: tileWidth }]}
                  >
                    <CourseTileButton
                      courseName={courseName}
                      theme={theme}
                      styles={styles}
                      nameToType={nameToType}
                      onPress={handleTilePress}
                      tileExtraStyle={{ maxWidth: tileWidth }}
                    />
                  </View>
                ))}
              </View>
            )}
          </>
        )}
      </ScrollView>

      <Modal
        visible={typeDropdownOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setTypeDropdownOpen(false)}
      >
        <View style={styles.modalRoot}>
          <Pressable
            style={styles.modalBackdrop}
            onPress={() => setTypeDropdownOpen(false)}
          />
          <View
            style={[
              styles.modalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
                borderColor: theme.isDarkMode ? theme.colors.border : '#dddbda',
              },
            ]}
            {...(Platform.OS === 'web'
              ? { dataSet: { myCoursesTypeModalSheet: 'true' } }
              : {})}
          >
            <Text
              style={[
                styles.modalTitle,
                !theme.isDarkMode && styles.modalTitleLight,
                { color: theme.colors.heading },
              ]}
            >
              Filter by course type
            </Text>
            <ScrollView
              style={[
                styles.modalList,
                !theme.isDarkMode && styles.modalListLight,
              ]}
              contentContainerStyle={
                !theme.isDarkMode ? styles.modalListContentLight : undefined
              }
              keyboardShouldPersistTaps="handled"
              {...(Platform.OS === 'web'
                ? { dataSet: { myCoursesTypeModalList: 'true' } }
                : {})}
            >
              {filterOptions.map((opt) => (
                <TouchableOpacity
                  key={opt}
                  style={[
                    styles.modalOption,
                    {
                      borderBottomColor: theme.isDarkMode ? '#444' : '#eee',
                      backgroundColor:
                        selectedCourseType === opt
                          ? theme.isDarkMode
                            ? 'rgba(212,175,55,0.15)'
                            : 'rgba(212,175,55,0.2)'
                          : theme.isDarkMode
                            ? 'transparent'
                            : '#FFFFFF',
                    },
                  ]}
                  onPress={() => {
                    setSelectedCourseType(opt);
                    setTypeDropdownOpen(false);
                  }}
                >
                  <Text
                    style={[
                      styles.modalOptionText,
                      {
                        color: theme.colors.text,
                        fontWeight: selectedCourseType === opt ? '700' : '400',
                      },
                    ]}
                  >
                    {opt}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!hotspotModal}
        transparent
        animationType="fade"
        onRequestClose={() => setHotspotModal(null)}
      >
        <View
          style={styles.modalRoot}
          {...(Platform.OS === 'web'
            ? { dataSet: { myCoursesHotspotModalRoot: 'true' } }
            : {})}
        >
          <Pressable
            style={styles.modalBackdrop}
            onPress={() => setHotspotModal(null)}
            {...(Platform.OS === 'web' ? { dataSet: { modalBackdrop: 'true' } } : {})}
          />
          <View
            style={[
              styles.hotspotModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
                borderColor: theme.isDarkMode ? theme.colors.border : '#dddbda',
              },
            ]}
            {...(Platform.OS === 'web'
              ? { dataSet: { myCoursesHotspotModalSheet: 'true' } }
              : {})}
          >
            <View style={styles.hotspotModalHeader}>
              <Text
                style={[styles.hotspotModalTitle, { color: theme.colors.heading }]}
                numberOfLines={2}
              >
                {hotspotModal?.hotspot?.label}
              </Text>
              <TouchableOpacity
                onPress={() => setHotspotModal(null)}
                style={styles.hotspotModalClose}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Text style={[styles.hotspotModalCloseText, { color: theme.colors.text }]}>
                  ✕
                </Text>
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.hotspotModalScroll}
              contentContainerStyle={styles.hotspotModalScrollContent}
              keyboardShouldPersistTaps="handled"
            >
              {hotspotModal &&
                (hotspotModal.matched.length === 0 ? (
                  <View style={styles.hotspotModalEmpty}>
                    <Text style={[styles.hotspotModalEmptyTitle, { color: theme.colors.heading }]}>
                      No matching courses on your profile
                    </Text>
                    <Text style={[styles.hotspotModalEmptySub, { color: theme.colors.text }]}>
                      This area links to:{' '}
                      {hotspotModal.hotspot.courseNames.join(', ')}. Add one of these to your
                      courses to see it here.
                    </Text>
                  </View>
                ) : (
                  hotspotModal.matched.map((courseName) => (
                    <CourseTileButton
                      key={courseName}
                      courseName={courseName}
                      theme={theme}
                      styles={styles}
                      nameToType={nameToType}
                      onPress={handleTilePress}
                      tileExtraStyle={styles.hotspotModalTile}
                      preferTouchable
                    />
                  ))
                ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme, tileWidth) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
      ...(Platform.OS === 'web' && !theme.isDarkMode
        ? { boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }
        : {}),
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    profileIconContainer: {
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    content: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    contentContainer: {
      paddingHorizontal: H_PADDING,
      paddingBottom: 40,
      paddingTop: 8,
      flexGrow: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    contentContainerWeb: Platform.select({
      web: {
        flexGrow: 1,
        minHeight: 'calc(100vh - 120px)',
      },
      default: {},
    }),
    filterBarRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginBottom: 16,
      gap: 12,
      alignSelf: 'stretch',
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    filterBarLeft: {
      flex: 1,
      minWidth: 0,
      maxWidth: 400,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    viewToggleWrap: {
      alignItems: 'flex-end',
      flexShrink: 0,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    viewToggleLabel: {
      fontSize: 11,
      fontWeight: '600',
      marginBottom: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    viewToggle: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    viewToggleShellDark: {
      borderRadius: 10,
      borderWidth: 2,
      borderColor: '#d4af37',
      padding: 4,
      gap: 0,
      backgroundColor: '#1e1e1e',
    },
    viewToggleShellLight: {
      borderRadius: 10,
      borderWidth: 2,
      borderColor: '#d4af37',
      padding: 4,
      gap: 0,
      backgroundColor: '#FFFFFF',
    },
    viewToggleHit: {
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
      width: 52,
      height: 48,
      minWidth: 52,
      minHeight: 48,
      paddingHorizontal: 0,
      paddingVertical: 0,
    },
    viewToggleHitWeb: Platform.select({
      web: {
        outlineWidth: 0,
        outlineStyle: 'none',
        cursor: 'pointer',
      },
      default: {},
    }),
    viewToggleHitSelected: {
      borderWidth: 2,
      borderColor: '#d4af37',
      backgroundColor: 'rgba(212, 175, 55, 0.38)',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 0 0 1px rgba(212, 175, 55, 0.5) inset' }
        : {}),
    },
    /** Selected tiles — white inset (light); dark: gold ring only on shell-grey / transparent tray */
    viewToggleHitSelectedTilesLight: !theme.isDarkMode
      ? {
          borderWidth: 3,
          borderColor: '#d4af37',
          backgroundColor: '#FFFFFF',
          ...(Platform.OS === 'web'
            ? { boxShadow: 'inset 0 0 0 1px rgba(212, 175, 55, 0.4)' }
            : {}),
        }
      : {
          borderWidth: 3,
          borderColor: '#d4af37',
          backgroundColor: 'transparent',
          ...(Platform.OS === 'web' ? { boxShadow: 'none' } : {}),
        },
    /** Selected body thumb — white tray (light); tinted + gold ring (dark) */
    viewToggleHitSelectedBodyLight: !theme.isDarkMode
      ? {
          borderWidth: 3,
          borderColor: '#d4af37',
          backgroundColor: '#FFFFFF',
        }
      : {
          borderWidth: 3,
          borderColor: '#d4af37',
          backgroundColor: 'rgba(212, 175, 55, 0.38)',
        },
    viewToggleHitUnselected: {
      borderWidth: 2,
      borderColor: 'transparent',
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    viewToggleDivider: {
      width: 2,
      alignSelf: 'stretch',
      marginVertical: 4,
      borderRadius: 1,
      opacity: 0.85,
    },
    viewToggleBodyThumbRing: {
      borderRadius: 6,
      padding: 0,
      borderWidth: 0,
      borderColor: 'transparent',
    },
    viewToggleBodyThumbRingSelected: {
      borderColor: 'transparent',
      borderWidth: 0,
    },
    viewToggleBodyThumb: {
      width: 44,
      height: 44,
      borderRadius: 4,
      backgroundColor: 'transparent',
      overflow: 'hidden',
    },
    bodyDummyWrap: {
      alignSelf: 'center',
      alignItems: 'center',
      marginTop: 4,
      minHeight: 200,
      maxWidth: 520,
      width: '100%',
    },
    bodyDummyImageShell: {
      position: 'relative',
      alignSelf: 'center',
      overflow: 'hidden',
      borderRadius: 8,
    },
    bodyHotspotLayer: {
      ...StyleSheet.absoluteFillObject,
    },
    bodyHotspotLayerAbove: {
      zIndex: 1,
      backgroundColor: 'transparent',
    },
    bodyHotspot: {
      position: 'absolute',
    },
    bodyHotspotPressableWeb: Platform.select({
      web: { cursor: 'pointer' },
      default: {},
    }),
    bodyHotspotPressed: {
      opacity: 0.88,
    },
    bodyHotspotCircle: {
      borderWidth: 2,
      backgroundColor: 'rgba(212, 175, 55, 0.18)',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 0 0 1px rgba(212, 175, 55, 0.45)' }
        : {
            shadowColor: '#d4af37',
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 3,
          }),
    },
    hotspotModalSheet: {
      maxHeight: '82%',
      width: '100%',
      maxWidth: 400,
      alignSelf: 'center',
      borderRadius: 12,
      borderWidth: 2,
      borderColor: '#d4af37',
      paddingHorizontal: 14,
      paddingTop: 12,
      paddingBottom: 16,
      position: 'relative',
      zIndex: 2,
      ...(Platform.OS === 'web' ? { boxShadow: '0 8px 28px rgba(0,0,0,0.35)' } : {}),
    },
    hotspotModalHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 12,
      gap: 8,
    },
    hotspotModalTitle: {
      flex: 1,
      fontSize: 17,
      fontWeight: '700',
      paddingRight: 8,
    },
    hotspotModalClose: {
      padding: 4,
    },
    hotspotModalCloseText: {
      fontSize: 22,
      fontWeight: '300',
    },
    hotspotModalScroll: {
      maxHeight: 420,
    },
    hotspotModalScrollContent: {
      paddingBottom: 8,
    },
    hotspotModalTile: {
      width: '100%',
      maxWidth: 320,
      alignSelf: 'center',
      marginBottom: 14,
    },
    hotspotModalEmpty: {
      paddingVertical: 12,
      paddingHorizontal: 4,
    },
    hotspotModalEmptyTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 8,
    },
    hotspotModalEmptySub: {
      fontSize: 14,
      lineHeight: 20,
    },
    filterLabel: {
      fontSize: 13,
      fontWeight: '600',
      marginBottom: 6,
    },
    filterTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 2,
    },
    filterTriggerText: {
      fontSize: 16,
      flex: 1,
      marginRight: 8,
    },
    filterChevron: {
      fontSize: 14,
    },
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 24,
      backgroundColor: 'transparent',
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.4)',
      zIndex: 0,
    },
    modalSheet: {
      maxHeight: '70%',
      borderRadius: 10,
      borderWidth: 1,
      paddingTop: 12,
      paddingBottom: 8,
      zIndex: 1,
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? { boxShadow: '0 8px 24px rgba(0,0,0,0.2)' } : {}),
    },
    modalTitle: {
      fontSize: 16,
      fontWeight: '700',
      paddingHorizontal: 16,
      paddingBottom: 10,
    },
    modalTitleLight: {
      backgroundColor: '#FFFFFF',
    },
    modalList: {
      maxHeight: 360,
    },
    modalListLight: {
      backgroundColor: '#FFFFFF',
    },
    modalListContentLight: {
      backgroundColor: '#FFFFFF',
      flexGrow: 1,
    },
    modalOption: {
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderBottomWidth: StyleSheet.hairlineWidth,
    },
    modalOptionText: {
      fontSize: 16,
    },
    tilesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      alignContent: 'flex-start',
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    tileWrap: {
      marginBottom: TILE_GAP,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    titleUnderlineWrap: {
      alignSelf: 'center',
      maxWidth: '100%',
      paddingBottom: 2,
      backgroundColor: 'transparent',
    },
    shinobiUnderlineStack: {
      alignSelf: 'stretch',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    shinobiUnderlineWhite: {
      height: 2,
      backgroundColor: '#ffffff',
      borderRadius: 1,
    },
    shinobiUnderlineBlack: {
      height: 2,
      backgroundColor: '#0a0a0a',
      borderRadius: 1,
      marginTop: 2,
    },
    tile: {
      width: '100%',
      height: 180,
      maxWidth: tileWidth,
      borderRadius: 14,
      padding: 20,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
      ...(Platform.OS === 'web'
        ? {
            backgroundColor: 'transparent',
          }
        : {
            backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
            borderWidth: 2,
            borderColor: 'rgba(212, 175, 55, 0.85)',
          }),
    },
    tileImage: {
      width: '100%',
      height: 80,
      marginBottom: 12,
    },
    tileTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      textAlign: 'center',
      marginBottom: 4,
    },
    emptyWrap: {
      paddingVertical: 40,
      paddingHorizontal: 16,
      alignItems: 'center',
    },
    emptyTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 8,
    },
    emptySub: {
      fontSize: 14,
      color: theme.colors.text,
      textAlign: 'center',
      lineHeight: 20,
      maxWidth: 320,
    },
  });
}
